'use client'

import { useId } from 'react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { ChevronDown, ChevronUp, Cookie, Shield, X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/encuentro/ui/switch'
import { useCookieConsentBanner } from '@/hooks/use-cookie-consent'

/**
 * The cookie-consent banner in Encuentro (the root layout mounts it instead of
 * the classic one when `data-ui="encuentro"` is on `<html>`). Same copy, same
 * choices and the same effects as the classic banner (`useCookieConsentBanner`);
 * only the look changes: a `surface` card with `radius-lg` and `shadow-overlay`,
 * the F4 buttons and the Encuentro `Switch` (ink when on, 44 px target). It sits above the TabBar (its height is published in
 * `--mobile-bottom-nav-height`), never over it, and scrolls inside itself when
 * the text is large.
 */
export function EncuentroCookieConsentBanner() {
  const t = useTranslations('encuentro.frame.cookies')
  const consent = useCookieConsentBanner()
  const titleId = useId()
  const detailsId = useId()

  if (!consent.isVisible) return null
  const { showDetails, preferences } = consent

  return (
    <div
      data-slot="cookie-consent"
      data-look="encuentro"
      className="pointer-events-none fixed inset-x-0 bottom-[max(var(--mobile-bottom-nav-height,0px),max(env(safe-area-inset-bottom,0px),var(--safe-area-inset-bottom,0px)))] z-50 flex justify-center p-2 sm:p-4 font-en-sans text-en-ink motion-safe:animate-in motion-safe:duration-300 motion-safe:slide-in-from-bottom"
    >
      <section
        aria-labelledby={titleId}
        className="pointer-events-auto grid max-h-[calc(var(--app-height,100dvh)-var(--mobile-bottom-nav-height,0px)-var(--en-size-topbar)-2rem)] w-full max-w-2xl grid-cols-[minmax(0,1fr)] gap-4 overflow-y-auto overscroll-contain rounded-en-lg bg-en-surface p-4 shadow-en-overlay sm:p-5"
      >
        <div className="flex items-start gap-3">
          {/* On the phone the title needs the width (text at 200 %): no icon there. */}
          <Cookie aria-hidden className="mt-1 hidden size-5 shrink-0 text-en-ink-muted sm:block" />
          <p
            id={titleId}
            className="min-w-0 flex-1 text-en-subheading [overflow-wrap:break-word] hyphens-auto"
          >
            {t('title')}
          </p>
          <Button
            look="encuentro"
            variant="ghost"
            iconOnly
            aria-label={t('close')}
            onClick={consent.close}
            className="-mt-2 -mr-2"
          >
            <X aria-hidden />
          </Button>
        </div>

        <p className="text-en-body text-en-ink-muted">{t('description')}</p>

        <Button
          look="encuentro"
          variant="ghost"
          size="sm"
          aria-expanded={showDetails}
          aria-controls={showDetails ? detailsId : undefined}
          onClick={consent.toggleDetails}
          className="-ml-3 justify-self-start"
        >
          {showDetails ? <ChevronUp aria-hidden /> : <ChevronDown aria-hidden />}
          {showDetails ? t('hideDetails') : t('showDetails')}
        </Button>

        {showDetails && (
          <div id={detailsId} className="grid gap-4 border-t border-en-line pt-4">
            <div className="flex items-start justify-between gap-4">
              <div className="grid min-w-0 gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Shield aria-hidden className="size-4 shrink-0 text-en-positive" />
                  <p className="text-en-body-strong">{t('essential')}</p>
                  <Badge look="encuentro" tone="positive">
                    {t('alwaysOn')}
                  </Badge>
                </div>
                <p className="text-en-meta text-en-ink-muted">{t('essentialDescription')}</p>
              </div>
              <Switch checked disabled onCheckedChange={() => {}} aria-label={t('essential')} />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="grid min-w-0 gap-1">
                <label htmlFor={`${detailsId}-functional`} className="text-en-body-strong">
                  {t('functional')}
                </label>
                <p className="text-en-meta text-en-ink-muted">{t('functionalDescription')}</p>
              </div>
              <Switch
                id={`${detailsId}-functional`}
                checked={preferences.functional}
                onCheckedChange={(checked) => consent.setPreference('functional', checked)}
              />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="grid min-w-0 gap-1">
                <label htmlFor={`${detailsId}-analytics`} className="text-en-body-strong">
                  {t('analytics')}
                </label>
                <p className="text-en-meta text-en-ink-muted">{t('analyticsDescription')}</p>
              </div>
              <Switch
                id={`${detailsId}-analytics`}
                checked={preferences.analytics}
                onCheckedChange={(checked) => consent.setPreference('analytics', checked)}
              />
            </div>
          </div>
        )}

        <div className="grid gap-2 sm:flex sm:flex-wrap sm:items-center">
          <Button look="encuentro" variant="secondary" onClick={consent.rejectNonEssential}>
            {showDetails ? t('essentialOnly') : t('rejectNonEssential')}
          </Button>
          <Button
            look="encuentro"
            variant="primary"
            onClick={showDetails ? consent.savePreferences : consent.acceptAll}
          >
            {showDetails ? t('savePreferences') : t('acceptAll')}
          </Button>
          <Link
            href="/policies/privacy"
            target="_blank"
            className="inline-flex min-h-en-control items-center justify-center rounded-en-md px-2 text-en-meta text-en-ink-muted underline-offset-4 hover:text-en-ink hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus sm:ml-auto"
          >
            {t('privacyPolicy')}
          </Link>
        </div>
      </section>
    </div>
  )
}
